import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Popover, Space } from 'antd';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { useMemo } from 'react';
import { ReactComponent as HelperSvgIcon } from './assets/helper.svg';
import { isTfsoftware } from 'productLine';
import tfWechatImage from 'assets/channels/tfsoftware/wechat.png';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';

const Item = styled.div`
  margin-bottom: 8px;
`;
const ItemTitle = styled.div`
  position: relative;
  font-size: 14px;
  font-weight: 500;
  padding-left: 12px;

  &:before {
    content: '';
    position: absolute;
    width: 3px;
    height: 12px;
    background-color: var(--ant-primary-color);
    top: 50%;
    transform: translateY(-50%);
    left: 0;
  }
`;
const ServicerText = styled.p`
  margin-bottom: 8px;
  color: #84868c;
`;
const QRCodeImg = styled.img`
  width: 160px;
  height: 160px;
`;

export interface AuthShopHelperProps {
  display?: 'icon' | 'content' | 'tips';
  contentItems?: ('servicer' | 'tkTips' | 'wechat' | 'failReason')[];
  onNeedHelp: () => void;
  noReason3?: boolean;
}

const AuthShopHelper = (props: AuthShopHelperProps) => {
  const {
    display = 'icon',
    contentItems = ['servicer', 'wechat', 'failReason'],
    onNeedHelp,
    noReason3 = false,
  } = props;
  const { contactQrcode } = useFlagConfigValue();
  const { t } = useTranslate();
  const sysLang = useSystemLanguageValue();

  const servicer = useMemo(
    () => (
      <Item key="servicer">
        <ItemTitle>{t('authShop.客户服务专家主动联系您，协助完成添加店铺')}</ItemTitle>
        <ServicerText>{t('authShop.工作日将在30分钟内联系')}</ServicerText>
        <Button type="primary" onClick={onNeedHelp}>
          {t('authShop.需要协助')}
        </Button>
      </Item>
    ),
    [onNeedHelp, t],
  );

  const wechat = useMemo(
    () => (
      <Item key="wechat">
        <ItemTitle>{t('authShop.微信扫一扫，添加客户服务专家')}</ItemTitle>
        <QRCodeImg src={isTfsoftware ? tfWechatImage : contactQrcode} alt="contactQrcode" />
      </Item>
    ),
    [contactQrcode, t],
  );

  const failReason = useMemo(
    () => (
      <Item key="failReason">
        <ItemTitle>{t('authShop.常见失败原因')}</ItemTitle>
        <ul>
          <li key={1}>{t('authShop.可能是帐号密码输入错误导致失败')}</li>
          <li key={2}>{t('authShop.选择了与帐号不符的站点')}</li>
          {noReason3 ? null : (
            <li key={3}>{t('authShop.店铺类型为跨境店铺，添加时选择了普通店铺类型')}</li>
          )}
        </ul>
      </Item>
    ),
    [t, noReason3],
  );

  const tkTips = useMemo(() => {
    const href =
      sysLang === 'zh-CN'
        ? 'https://leyan.yuque.com/czpqnb/nmiwyb/ridt4s233lvey3a9#Ej1Uw'
        : 'https://chatplus.ganjutech.com/wiki/en/#faq-15';
    return (
      <Item key="tkTips">
        <ItemTitle>{t('常见授权失败提示')}</ItemTitle>
        <ul>
          <li key={1}>
            {t('TikTok提示')}
            <a href={href} target="_blank" rel="noreferrer" title="Oops Something went wrong">
              "Oops Something went wrong"
            </a>
          </li>
          <li key={2}>
            {t('TikTok提示')}
            <a
              href={href}
              title="You have an active authorisation of this service. The authorisation has been redone. Please check with your partner."
              target="_blank"
              rel="noreferrer"
            >
              "You have an active authorisation of this service..."
            </a>
          </li>
        </ul>
      </Item>
    );
  }, [t, sysLang]);

  const contentRender = useMemo(
    () => ({
      servicer,
      wechat,
      tkTips,
      failReason,
    }),
    [failReason, servicer, wechat, tkTips],
  );

  const content = useMemo(() => {
    return contentItems.map((item) => contentRender[item]);
  }, [contentItems, contentRender]);

  const overlayInnerStyle = {
    maxHeight: '366px',
    overflowY: 'auto',
    overflowX: 'hidden',
  } as const;

  if (display === 'icon') {
    return (
      <Popover overlayInnerStyle={overlayInnerStyle} content={content}>
        <Icon style={{ fontSize: 64 }} component={HelperSvgIcon} />
      </Popover>
    );
  }

  if (display === 'content') {
    return <>{content}</>;
  }

  if (display === 'tips') {
    return (
      <Popover overlayInnerStyle={overlayInnerStyle} content={content}>
        <Space align="center">
          <Icon style={{ fontSize: 24 }} component={HelperSvgIcon} />
          {t('authShop.添加大量店铺，需要协助')}
        </Space>
      </Popover>
    );
  }

  return <></>;
};

export default AuthShopHelper;
